import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async"; // ✅ SEO ONLY ADD
import { motion } from "framer-motion";
import { Shield, Wrench, Cpu, CheckCircle, Zap } from "lucide-react";
import "./About.css";
import InnerBanner from "../components/InnerBanner";
import serviceBg from "../assets/FFO+.jpeg";

const AboutPage = () => {

  const useCounter = (end, duration = 2000) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      let start = 0;
      const increment = end / (duration / 16);

      const counter = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(counter);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(counter);
    }, [end, duration]);

    return count;
  };

  const years = useCounter(12);
  const repairs = useCounter(45);
  const success = useCounter(99.8);

  const containerVariant = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.15 }
    }
  };

  const cardVariant = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const heroVariant = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  return (
    <>
      {/* ✅ SEO ONLY (NO DESIGN CHANGE) */}
      <Helmet>
        <title>About Servizy | Mobile Repair Experts</title>

        <meta
          name="description"
          content="Servizy offers mobile repair services with doorstep support, expert technicians, and high success rate across India."
        />

        <meta
          name="keywords"
          content="mobile repair, doorstep repair, phone repair India, Servizy"
        />

        <link rel="canonical" href="https://servizyweb.netlify.app/about" />

        {/* Open Graph */}
        <meta property="og:title" content="About Servizy" />
        <meta property="og:description" content="Trusted repair service at your doorstep." />
        <meta property="og:image" content="https://servizyweb.netlify.app/s.png" />
        <meta property="og:url" content="https://servizyweb.netlify.app/about" />
        <meta property="og:type" content="website" />
      </Helmet>

      <br/>
      <br/>

      <InnerBanner title="About Us" bgImage={serviceBg} />

      <div className="about-wrapper">

        {/* HERO */}
        <motion.section
          className="hero-section"
          variants={heroVariant}
          initial="hidden"
          animate="visible"
        >
          <span className="pill">SINCE 2014</span>

          <h1 className="main-title">
            We Repair. <br />
            <span className="highlight">You Reconnect.</span>
          </h1>

          <p className="hero-sub">
            More than just technicians, we are hardware specialists dedicated
            to extending the life of the devices you love.
          </p>
        </motion.section>

        {/* STATS */}
        <motion.section
          className="about-stats-container"
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            className="about-stat-card floating"
            variants={cardVariant}
            whileHover={{ y: -12, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <div className="stat-top">
              <Zap size={28} />
              <h2>{years}+</h2>
            </div>
            <p>Years Experience</p>
          </motion.div>

          <motion.div
            className="about-stat-card floating"
            variants={cardVariant}
            whileHover={{ y: -12, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <div className="stat-top">
              <Wrench size={28} />
              <h2>{repairs}k+</h2>
            </div>
            <p>Total Repairs</p>
          </motion.div>

          <motion.div
            className="about-stat-card floating"
            variants={cardVariant}
            whileHover={{ y: -12, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <div className="stat-top">
              <Cpu size={28} />
              <h2>{success}%</h2>
            </div>
            <p>Success Rate</p>
          </motion.div>
        </motion.section>

        {/* MISSION & VISION */}
        <motion.section
          className="mission-grid"
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div className="mission-box floating" variants={cardVariant}>
            <h3>Our Mission</h3>
            <br />
            <p>
              To reduce global e-waste by providing elite-grade repair services
              that make devices last longer.
            </p>
          </motion.div>

          <motion.div className="mission-box floating" variants={cardVariant}>
            <h3>Our Vision</h3>
            <br />
            <p>
              To set the gold standard in tech-repair transparency and data
              security worldwide.
            </p>
          </motion.div>
        </motion.section>

        {/* SAFETY */}
        <motion.section className="safety-vault floating">
          <div className="vault-header">
            <Shield size={48} />
            <h2>The Zero-Access Policy</h2>
          </div>

          <p>
            Your privacy is non-negotiable. Our workshop uses hardware-isolated
            testing environments to ensure your personal data never leaves your device.
          </p>

          <div className="safety-features">
            <div className="feat"><CheckCircle size={16} /> No Data Retention</div>
            <div className="feat"><CheckCircle size={16} /> ISO-Certified Workflows</div>
            <div className="feat"><CheckCircle size={16} /> ESD-Safe Environment</div>
          </div>

          <div className="encryption-loader">
            <motion.div
              className="loader-fill"
              initial={{ width: 0 }}
              whileInView={{ width: "100%" }}
              transition={{ duration: 2 }}
            />
          </div>

          <span className="status-text">
            SECURE REPAIR PROTOCOL ACTIVE
          </span>
        </motion.section>

      </div>
    </>
  );
};

export default AboutPage;